import SettingsPageLayout, { SettingsPageLoading, settingsPageTitleClass } from "../../../components/shared/SettingsPageLayout";
import React from "react";
import { CompanyActivityAndPayments, ApproveDeletionDialog, DeletionJobs } from "./InternalCompanyOperations";
import RightworkSelect from "../../../components/shared/RightworkSelect";
import { CompanyOverdueStatus, PaymentReminderDialog, ReminderHistory, invoiceMoney, overdueSummary } from "./InternalPaymentReminders";
import {
  COMPANY_FILTERS, COMPANY_STATUS_LABELS, filterCompanies,
  CompanyPeopleDetails, DeletionRequestDialog, StripeSetupDialog,
} from "./InternalCompanyOverviewTools";
import AttachMoneyRoundedIcon from "@mui/icons-material/AttachMoneyRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import CreditCardRoundedIcon from "@mui/icons-material/CreditCardRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import ExpandLessRoundedIcon from "@mui/icons-material/ExpandLessRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import StoreRoundedIcon from "@mui/icons-material/StoreRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import { toast } from "react-toastify";

import {
  API_approve_internal_company_deletion,
  API_update_internal_company_deletion_job,
  API_update_internal_company_deletion_request,
  API_complete_internal_billing_request,
  API_create_internal_stripe_customer,
  API_get_internal_billing_overview,
  API_refresh_internal_company_payment_health,
  API_update_internal_billing_rates,
  API_update_internal_store_status,
} from "../../../constants";
import { getRightworkAPI, postRightworkAPI } from "../requests/utilities/requests";


const STORE_STATUS_OPTIONS = [
  { value: "open", label: "Open" },
  { value: "closed", label: "Closed" },
];

const STORE_ONBOARDING_STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "setting_up", label: "Setting up" },
  { value: "inactive", label: "Inactive" },
];


function formatMoney(cents, currency = "usd") {
  if (cents === null || cents === undefined) {
    return "Unset";
  }
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: (currency || "usd").toUpperCase(),
  }).format(cents / 100);
}


function centsToDollars(cents) {
  if (cents === null || cents === undefined) {
    return "";
  }
  return (Number(cents) / 100).toFixed(2);
}


function dollarsToCents(value) {
  const trimmedValue = String(value || "").trim();
  if (!trimmedValue) {
    return null;
  }
  const normalizedValue = trimmedValue.replace(/[$,]/g, "");
  const parsedValue = Number(normalizedValue);
  if (Number.isNaN(parsedValue) || parsedValue < 0) {
    throw new Error("Enter a valid non-negative rate.");
  }
  return Math.round(parsedValue * 100);
}


function formatDateTime(value) {
  if (!value) {
    return "Not set";
  }
  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) {
    return value;
  }
  return parsedDate.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}


function getStatusBadge(location) {
  if (location.is_billable) {
    return "border-emerald-200 bg-emerald-50 text-emerald-700";
  }
  if (location.status === "closed") {
    return "border-rose-200 bg-rose-50 text-rose-700";
  }
  if (location.onboarding_status === "setting_up") {
    return "border-amber-200 bg-amber-50 text-amber-700";
  }
  return "border-slate-200 bg-slate-50 text-slate-600";
}


function getOptionLabel(options, value) {
  return options.find((option) => option.value === value)?.label || value || "Unknown";
}


function getBillingStatusLabel(store) {
  if (store.is_billable) {
    return "Billable";
  }
  if (store.status === "closed") {
    return "Closed";
  }
  return getOptionLabel(STORE_ONBOARDING_STATUS_OPTIONS, store.onboarding_status);
}


function getRateEditableStores(company) {
  return (company?.stores || []).filter((store) => store.status === "open");
}


function getStripeLabel(stripeContext) {
  const status = stripeContext?.status;
  if (status === "available") {
    return "Invoice found";
  }
  if (status === "unavailable") {
    return "Stripe unavailable";
  }
  if (status === "not_loaded") {
    return "Invoice not loaded";
  }
  if (status === "missing_customer_id") {
    return "Missing customer";
  }
  if (status === "no_invoices") {
    return "No invoices";
  }
  if (status === "failed") {
    return "Stripe error";
  }
  return "Unknown";
}


function getStripeConnectionBadge(company) {
  if (company?.stripe_connected) {
    return {
      label: "Connected",
      className: "border-emerald-200 bg-emerald-50 text-emerald-700",
    };
  }
  return {
    label: "Missing customer",
    className: "border-amber-200 bg-amber-50 text-amber-700",
  };
}


function getExpectedMonthlyDisplay(company) {
  const totalsByCurrency = company?.expected_monthly_totals_by_currency || {};
  const currencyEntries = Object.entries(totalsByCurrency)
    .filter(([, amount]) => amount !== null && amount !== undefined)
    .sort(([leftCurrency], [rightCurrency]) => leftCurrency.localeCompare(rightCurrency));
  const amount = company?.expected_monthly_total?.amount_cents || 0;
  const currency = company?.expected_monthly_total?.currency || "usd";
  const missingRateCount = company?.missing_rate_store_count || 0;
  const missingRateDetail = `${missingRateCount} billable ${
    missingRateCount === 1 ? "store" : "stores"
  } missing rates`;

  if (currencyEntries.length > 1) {
    return {
      primary: missingRateCount > 0 ? "Mixed currencies + missing rates" : "Mixed currencies",
      detail: `${currencyEntries
        .map(([entryCurrency, entryAmount]) => formatMoney(entryAmount, entryCurrency))
        .join(" + ")}${missingRateCount > 0 ? `; ${missingRateDetail}` : ""}`,
      className: "text-amber-700",
    };
  }

  if (missingRateCount > 0 && amount > 0) {
    return {
      primary: `${formatMoney(amount, currency)} + missing rates`,
      detail: missingRateDetail,
      className: "text-amber-700",
    };
  }
  if (missingRateCount > 0) {
    return {
      primary: "Missing rate",
      detail: missingRateDetail,
      className: "text-amber-700",
    };
  }
  return {
    primary: formatMoney(amount, currency),
    detail: null,
    className: "text-slate-900",
  };
}


function getSummaryExpectedMonthlyDisplay(summary) {
  const totalsByCurrency = summary?.expected_monthly_totals_by_currency || {};
  const currencyEntries = Object.entries(totalsByCurrency)
    .filter(([, amount]) => amount !== null && amount !== undefined)
    .sort(([leftCurrency], [rightCurrency]) => leftCurrency.localeCompare(rightCurrency));

  if (currencyEntries.length > 1) {
    return {
      value: "Mixed currencies",
      detail: currencyEntries
        .map(([currency, amount]) => formatMoney(amount, currency))
        .join(" + "),
    };
  }

  if (currencyEntries.length === 1) {
    const [currency, amount] = currencyEntries[0];
    return {
      value: formatMoney(amount, currency),
      detail: "From Rightwork rates",
    };
  }

  return {
    value: formatMoney(summary?.expected_monthly_total_cents || 0),
    detail: "From Rightwork rates",
  };
}


function SummaryCard({ icon: Icon, title, value, detail }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex items-center gap-3">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-700">
          <Icon fontSize="small" />
        </span>
        <div className="min-w-0">
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
            {title}
          </p>
          <p className="mt-1 truncate text-xl font-semibold text-slate-900">{value}</p>
          {detail && <p className="mt-0.5 truncate text-xs text-slate-500">{detail}</p>}
        </div>
      </div>
    </div>
  );
}


function RateEditModal({ company, onClose, onSave, saving }) {
  const [defaultRate, setDefaultRate] = React.useState(
    centsToDollars(company?.default_monthly_store_rate?.amount_cents)
  );
  const [currency, setCurrency] = React.useState(company?.billing_rate_currency || "usd");
  const [storeOverrides, setStoreOverrides] = React.useState(() => {
    const initialOverrides = {};
    getRateEditableStores(company).forEach((store) => {
      initialOverrides[store.store_id] = store.rate_override
        ? centsToDollars(store.rate_override.monthly_store_rate_cents)
        : "";
    });
    return initialOverrides;
  });

  React.useEffect(() => {
    setDefaultRate(centsToDollars(company?.default_monthly_store_rate?.amount_cents));
    setCurrency(company?.billing_rate_currency || "usd");

    const nextOverrides = {};
    getRateEditableStores(company).forEach((store) => {
      nextOverrides[store.store_id] = store.rate_override
        ? centsToDollars(store.rate_override.monthly_store_rate_cents)
        : "";
    });
    setStoreOverrides(nextOverrides);
  }, [company]);

  if (!company) {
    return null;
  }

  const rateEditableStores = getRateEditableStores(company);

  const updateStoreOverride = (storeId, value) => {
    setStoreOverrides((current) => ({
      ...current,
      [storeId]: value,
    }));
  };

  const handleSave = () => {
    try {
      const payload = {
        company_id: company.company_id,
        default_monthly_store_rate_cents: dollarsToCents(defaultRate),
        billing_rate_currency: currency,
        store_rate_overrides: rateEditableStores
          .map((store) => ({
            store_id: store.store_id,
            monthly_store_rate_cents: dollarsToCents(storeOverrides[store.store_id]),
            currency: store.rate_override?.currency || currency,
          }))
          .filter((override) => override.monthly_store_rate_cents !== null),
      };
      onSave(payload);
    } catch (error) {
      toast.error(error.message || "Unable to save rates.", { autoClose: 3000 });
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/45 px-4 py-6">
      <div className="max-h-[88vh] w-full max-w-3xl overflow-hidden rounded-xl bg-white shadow-2xl">
        <div className="border-b border-slate-200 px-5 py-4">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="text-lg font-semibold text-slate-900">
                Edit billing rates
              </h2>
              <p className="mt-1 text-sm text-slate-500">{company.company_name}</p>
            </div>
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-60"
            >
              Close
            </button>
          </div>
        </div>

        <div className="max-h-[64vh] overflow-y-auto px-5 py-4">
          <div className="grid grid-cols-[1fr_160px] gap-3 md:grid-cols-1">
            <label>
              <span className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
                Company default monthly store rate
              </span>
              <input
                value={defaultRate}
                onChange={(event) => setDefaultRate(event.target.value)}
                placeholder="250.00"
                className="mt-1.5 w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-violet-300"
              />
            </label>
            <label>
              <span className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
                Currency
              </span>
              <input
                value={currency}
                onChange={(event) => setCurrency(event.target.value.toLowerCase())}
                maxLength={3}
                className="mt-1.5 w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm uppercase outline-none focus:border-violet-300"
              />
            </label>
          </div>

          <div className="mt-5">
            <div className="mb-2 flex items-center justify-between gap-3">
              <h3 className="text-sm font-semibold text-slate-900">Store overrides</h3>
              <p className="text-xs text-slate-500">Leave blank to use the company default.</p>
            </div>
            <div className="overflow-hidden rounded-lg border border-slate-200">
              {rateEditableStores.length > 0 ? (
                rateEditableStores.map((store, index) => (
                  <div
                    key={store.store_id}
                    className={`grid grid-cols-[1fr_180px] items-center gap-4 px-4 py-3 md:grid-cols-1 ${
                      index !== rateEditableStores.length - 1 ? "border-b border-slate-100" : ""
                    }`}
                  >
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-slate-900">
                        {store.store_name}
                      </p>
                      <p className="text-xs text-slate-500">
                        Current {formatMoney(
                          store.effective_monthly_rate.amount_cents,
                          store.effective_monthly_rate.currency
                        )} - {store.rate_source.replace("_", " ")}
                      </p>
                    </div>
                    <input
                      value={storeOverrides[store.store_id] || ""}
                      onChange={(event) => updateStoreOverride(store.store_id, event.target.value)}
                      placeholder="Default"
                      className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-violet-300"
                    />
                  </div>
                ))
              ) : (
                <div className="px-4 py-3 text-sm text-slate-500">
                  No open stores available for rate overrides.
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 border-t border-slate-200 px-5 py-4">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="rounded-lg border border-slate-200 px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center gap-2 rounded-lg bg-violet-700 px-4 py-2.5 text-sm font-semibold text-white hover:bg-violet-800 disabled:bg-violet-300"
          >
            <AttachMoneyRoundedIcon fontSize="small" />
            {saving ? "Saving..." : "Save rates"}
          </button>
        </div>
      </div>
    </div>
  );
}


export default function InternalBillingOverviewDisplay() {
  const [approvingCompany, setApprovingCompany] = React.useState(null);
  const [savingJob, setSavingJob] = React.useState(false);
  const [search, setSearch] = React.useState("");
  const [filter, setFilter] = React.useState("all");
  const [deletingCompany, setDeletingCompany] = React.useState(null);
  const [stripeSetupCompany, setStripeSetupCompany] = React.useState(null);
  const [savingDeletion, setSavingDeletion] = React.useState(false);
  const [loadError, setLoadError] = React.useState(false);
  const [overview, setOverview] = React.useState(null);
  const [reminderCompany, setReminderCompany] = React.useState(null);
  const [scan, setScan] = React.useState(null);
  const scanControl = React.useRef(null);
  React.useEffect(() => () => { if (scanControl.current) scanControl.current.cancelled = true; scanControl.current = null; }, []);
  const updateHealth = React.useCallback((companyId, health) => {
    if (!health) return;
    setOverview((current) => {
      if (!current) return current;
      const customer = current.companies.find((item) => item.company_id === companyId)?.billing_settings?.stripe_customer_id?.trim();
      return { ...current, companies: current.companies.map((item) => item.company_id === companyId || (customer && item.billing_settings?.stripe_customer_id?.trim() === customer) ? { ...item, payment_health: health } : item) };
    });
  }, []);
  const recordReminder = (companyId, record) => {
    setOverview((current) => ({ ...current, companies: current.companies.map((item) => item.company_id === companyId ? { ...item, payment_reminders: [record, ...(item.payment_reminders || []).filter((row) => row.uuid !== record.uuid)].slice(0, 10) } : item) }));
  };
  const refreshBalances = async () => {
    const targets = [...new Map((overview?.companies || []).filter((item) => item.stripe_connected).map((item) => [item.billing_settings?.stripe_customer_id?.trim() || item.company_id, item])).values()];
    const control = { cancelled: false };
    scanControl.current = control;
    let done = 0, failures = 0;
    setScan({ done, total: targets.length, failures, running: true });
    // Explicit operator refresh, two customers at a time; directory loading
    // still only reads cached snapshots and never fans out to Stripe.
    const refreshCompany = async (company) => {
        let health;
        try {
          const response = await getRightworkAPI(API_refresh_internal_company_payment_health, { company_id: company.company_id });
          health = response.data.redux.payload.payment_health;
          if (health.refresh_error || health.refreshing || health.status !== "available") failures += 1;
        } catch (failure) {
          failures += 1;
          health = { ...company.payment_health, stale: true, refresh_error: "Refresh failed. Saved balance may be out of date." };
        }
        done += 1;
        if (!control.cancelled) { updateHealth(company.company_id, health); setScan({ done, total: targets.length, failures, running: true }); }
    };
    for (let index = 0; index < targets.length && !control.cancelled; index += 2) {
      await Promise.all(targets.slice(index, index + 2).map(refreshCompany));
    }
    if (scanControl.current === control) setScan({ done, total: targets.length, failures, running: false, stopped: control.cancelled });
  };
  const [loading, setLoading] = React.useState(true);
  const [refreshing, setRefreshing] = React.useState(false);
  const [savingRates, setSavingRates] = React.useState(false);
  const [completingRequestKey, setCompletingRequestKey] = React.useState(null);
  const [creatingStripeCustomerCompanyId, setCreatingStripeCustomerCompanyId] = React.useState(null);
  const [updatingStoreStatusId, setUpdatingStoreStatusId] = React.useState(null);
  const [expandedCompanyIds, setExpandedCompanyIds] = React.useState(new Set());
  const [editingCompany, setEditingCompany] = React.useState(null);

  const fetchOverview = React.useCallback(async ({ silent = false } = {}) => {
    if (silent) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    try {
      setLoadError(false);
      const response = await getRightworkAPI(API_get_internal_billing_overview);
      setOverview(response?.data?.redux?.payload || null);
    } catch (error) {
      setLoadError(true);
      toast.error(
        error?.response?.data?.system?.message || "Unable to load company overview.",
        { autoClose: 3500 }
      );
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  React.useEffect(() => {
    fetchOverview();
  }, [fetchOverview]);

  const hasActiveJobs = overview?.deletion_jobs?.some((job) => {
    if (job.status === "cancelled") {
      return !job.error && Object.values(job.notice_results || {}).some((notice) =>
        ["sent", "sending"].includes(notice.status) && notice.cancellation_status !== "sent"
      );
    }
    return !["completed", "failed", "review_changed"].includes(job.status);
  });
  React.useEffect(() => {
    if (!hasActiveJobs) return undefined;
    const timer = setInterval(() => fetchOverview({ silent: true }), 15000);
    return () => clearInterval(timer);
  }, [hasActiveJobs, fetchOverview]);

  const saveJobAction = async (endpoint, payload) => {
    setSavingJob(true);
    try {
      await postRightworkAPI(endpoint, payload);
      setApprovingCompany(null);
      await fetchOverview({ silent: true });
      toast.success("Deletion job updated.");
    } catch (error) {
      toast.error(error?.response?.data?.system?.message || "Unable to update deletion job.");
    } finally { setSavingJob(false); }
  };

  const toggleCompany = (companyId) => {
    setExpandedCompanyIds((current) => {
      const next = new Set(current);
      if (next.has(companyId)) {
        next.delete(companyId);
      } else {
        next.add(companyId);
      }
      return next;
    });
  };

  const saveRates = async (payload) => {
    setSavingRates(true);
    try {
      const response = await postRightworkAPI(API_update_internal_billing_rates, payload);
      setOverview(response?.data?.redux?.payload || null);
      setEditingCompany(null);
      toast.success("Billing rates updated.", { autoClose: 2500 });
    } catch (error) {
      toast.error(
        error?.response?.data?.system?.message || "Unable to update billing rates.",
        { autoClose: 3500 }
      );
    } finally {
      setSavingRates(false);
    }
  };

  const completeBillingRequest = async (company, request) => {
    const requestKey = `${company.company_id}-${request.request_index}`;
    setCompletingRequestKey(requestKey);
    try {
      const response = await postRightworkAPI(API_complete_internal_billing_request, {
        company_id: company.company_id,
        request_index: request.request_index,
      });
      setOverview(response?.data?.redux?.payload || null);
      toast.success("Billing request marked complete.", { autoClose: 2500 });
    } catch (error) {
      toast.error(
        error?.response?.data?.system?.message || "Unable to complete billing request.",
        { autoClose: 3500 }
      );
    } finally {
      setCompletingRequestKey(null);
    }
  };

  const createStripeCustomer = async (company, accountingPrimaryEmail) => {
    if (!accountingPrimaryEmail) {
      toast.error("Add a primary billing email before creating a Stripe customer.", {
        autoClose: 3500,
      });
      return;
    }

    setCreatingStripeCustomerCompanyId(company.company_id);
    try {
      const response = await postRightworkAPI(API_create_internal_stripe_customer, {
        company_id: company.company_id,
        accounting_primary_email: accountingPrimaryEmail,
      });
      const payload = response?.data?.redux?.payload || null;
      const createdCustomerId = payload?.stripe_create_result?.stripe_customer_id;
      setOverview(payload);
      setStripeSetupCompany(null);
      toast.success(
        createdCustomerId
          ? `Stripe customer created (${createdCustomerId}).`
          : "Stripe customer created.",
        { autoClose: 2500 }
      );
    } catch (error) {
      toast.error(
        error?.response?.data?.system?.message || "Unable to create Stripe customer.",
        { autoClose: 3500 }
      );
    } finally {
      setCreatingStripeCustomerCompanyId(null);
    }
  };

  const updateStoreStatus = async (company, store, fieldName, value) => {
    if (store[fieldName] === value) {
      return;
    }

    setUpdatingStoreStatusId(store.store_id);
    try {
      const response = await postRightworkAPI(API_update_internal_store_status, {
        company_id: company.company_id,
        store_id: store.store_id,
        [fieldName]: value,
      });
      setOverview(response?.data?.redux?.payload || null);
      toast.success("Store status updated.", { autoClose: 2000 });
    } catch (error) {
      toast.error(
        error?.response?.data?.system?.message || "Unable to update store status.",
        { autoClose: 3500 }
      );
    } finally {
      setUpdatingStoreStatusId(null);
    }
  };

  const updateDeletion = async (payload) => {
    setSavingDeletion(true);
    try {
      const response = await postRightworkAPI(API_update_internal_company_deletion_request, payload);
      setOverview(response?.data?.redux?.payload || null);
      setDeletingCompany(null);
      toast.success(payload.action === "request" ? "Deletion requested for internal review." : "Deletion request cancelled.");
    } catch (error) {
      toast.error(error?.response?.data?.system?.message || "Unable to update deletion request.");
    } finally {
      setSavingDeletion(false);
    }
  };

  if (loading && !overview) {
    return (
      <SettingsPageLoading />
    );
  }

  const summary = overview?.summary || {};
  const companies = overview?.companies || [];
  const collections = overdueSummary(companies);
  const visibleCompanies = filterCompanies(companies, search, filter);
  const needsInvoiceCompanies = companies.filter((company) => company.needs_invoice)
    .sort((left, right) => (left.oldest_pending_request_at || "").localeCompare(right.oldest_pending_request_at || ""));
  const summaryExpectedMonthly = getSummaryExpectedMonthlyDisplay(summary);
  const expectedMonthlyDetail = summary.missing_rate_stores
    ? `${summary.missing_rate_stores} billable ${summary.missing_rate_stores === 1 ? "store" : "stores"} missing rates`
    : summaryExpectedMonthly.detail;

  return (
    <SettingsPageLayout>
      <div className="space-y-6">
        <div className="flex items-start justify-between gap-4 md:flex-col md:items-stretch">
          <div>
            <h1 className={settingsPageTitleClass}>
              Internal company overview
            </h1>
            <p className="mt-1 text-sm text-slate-500">
              Billing, company status, team adoption, and account administration.
            </p>
          </div>
          <button
            type="button"
            onClick={() => fetchOverview({ silent: true })}
            disabled={refreshing}
            className="inline-flex h-9 items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-3 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-60"
          >
            <RefreshRoundedIcon fontSize="small" />
            {refreshing ? "Refreshing..." : "Refresh"}
          </button>
        </div>

        {loadError && <div role="alert" className="rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">
          Unable to refresh company data. {overview ? "Showing the last loaded overview." : "Use Refresh to try again."}
        </div>}

        <section aria-label="Overdue balance summary" className="rounded-xl border border-rose-200 bg-rose-50 p-5">
          <div className="flex items-start justify-between gap-4 md:flex-col">
            <div>
              <h2 className="text-base font-semibold text-rose-950">Overdue invoices</h2>
              <p className="mt-2 text-2xl font-semibold text-rose-800">{Object.entries(collections.totals).map(([currency, amount]) => invoiceMoney(amount, currency)).join(" + ") || (!overview || collections.unchecked ? "Balance not verified" : "No overdue balance")}</p>
              <p className="mt-1 text-sm text-rose-900">{collections.overdueCompanies} {collections.overdueCompanies === 1 ? "company" : "companies"} with known overdue invoices</p>
              <p className="mt-2 text-xs text-slate-600">Cached Stripe balances; shared customers counted once. {collections.unchecked ? `${collections.unchecked} connected companies need verification; totals may be incomplete or out of date.` : "All connected companies have a recent complete snapshot."}</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <button type="button" onClick={() => { setSearch(""); setFilter("overdue"); }} className="rounded-lg bg-rose-700 px-3 py-2 text-sm font-semibold text-white hover:bg-rose-800">View overdue companies</button>
              <button type="button" onClick={refreshBalances} disabled={scan?.running || !companies.some((item) => item.stripe_connected)} className="rounded-lg border border-rose-200 bg-white px-3 py-2 text-sm font-medium text-rose-900 disabled:opacity-50">{scan?.running ? `Checking ${scan.done} / ${scan.total}…` : "Refresh overdue balances"}</button>
              {scan?.running && <button type="button" disabled={scan.stopped} onClick={() => { scanControl.current.cancelled = true; setScan((current) => ({ ...current, stopped: true })); }} className="text-sm font-medium text-slate-700 underline">Stop refresh</button>}
            </div>
          </div>
          {scan && <p role="status" className="mt-3 text-xs text-slate-600">{scan.stopped ? "Refresh stopped." : scan.running ? "Checking Stripe with two requests at a time." : `Checked ${scan.done} Stripe customers.`} {scan.failures > 0 ? `${scan.failures} snapshots still need verification.` : ""}</p>}
        </section>

        <div className="grid grid-cols-5 gap-3 2xl:grid-cols-3 lg:grid-cols-2 md:grid-cols-1">
          <SummaryCard
            icon={ReceiptLongRoundedIcon}
            title="Pending requests"
            value={summary.pending_invoice_requests || 0}
            detail={`${summary.companies_needing_invoice || 0} companies need review`}
          />
          <SummaryCard
            icon={StoreRoundedIcon}
            title="Billable stores"
            value={summary.billable_stores || 0}
            detail={`${summary.missing_rate_stores || 0} missing rates`}
          />
          <SummaryCard
            icon={AttachMoneyRoundedIcon}
            title="Expected monthly"
            value={summaryExpectedMonthly.value}
            detail={expectedMonthlyDetail}
          />
          <SummaryCard
            icon={OpenInNewRoundedIcon}
            title="Stripe connected"
            value={summary.stripe_connected_companies || 0}
            detail={`${summary.stripe_missing_customer_companies || 0} missing customers`}
          />
          <SummaryCard
            icon={WarningAmberRoundedIcon}
            title="Companies"
            value={summary.company_count || companies.length}
            detail={`${summary.deletion_requested_companies || 0} deletion requests`}
          />
        </div>

        <section className="rounded-lg border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-200 px-5 py-4">
            <h2 className="text-base font-semibold text-slate-900">Needs invoice</h2>
            <p className="mt-1 text-sm text-slate-500">
              Companies with open add/remove billing requests, sorted oldest first.
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">Company</th>
                  <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">Oldest request</th>
                  <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">Requests</th>
                  <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">Monthly total</th>
                  <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">Stripe</th>
                  <th className="px-5 py-3 text-right text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {needsInvoiceCompanies.length > 0 ? (
                  needsInvoiceCompanies.map((company) => {
                    const stripeConnectionBadge = getStripeConnectionBadge(company);
                    const expectedMonthly = getExpectedMonthlyDisplay(company);
                    return (
                      <tr key={company.company_id}>
                        <td className="px-5 py-4">
                          <p className="text-sm font-semibold text-slate-900">{company.company_name}</p>
                          <p className="mt-0.5 text-xs text-slate-500">
                            {company.billable_store_count} billable stores
                          </p>
                        </td>
                        <td className="px-5 py-4 text-sm text-slate-700">
                          {formatDateTime(company.oldest_pending_request_at)}
                        </td>
                        <td className="px-5 py-4 text-sm text-slate-700">
                          {company.pending_billing_requests.length}
                        </td>
                        <td className="px-5 py-4 text-sm font-medium text-slate-900">
                          <p className={`text-sm font-semibold ${expectedMonthly.className}`}>
                            {expectedMonthly.primary}
                          </p>
                          {expectedMonthly.detail && (
                            <p className="mt-0.5 text-xs font-normal text-amber-700">
                              {expectedMonthly.detail}
                            </p>
                          )}
                        </td>
                        <td className="px-5 py-4">
                          <div className="flex flex-col items-start gap-1">
                            <span className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-medium ${stripeConnectionBadge.className}`}>
                              {stripeConnectionBadge.label}
                            </span>
                            <span className="text-xs text-slate-500">
                              {getStripeLabel(company.stripe_context)}
                            </span>
                          </div>
                        </td>
                        <td className="px-5 py-4 text-right">
                          <button
                            type="button"
                            onClick={() => { setSearch(""); setFilter("all"); toggleCompany(company.company_id); }}
                            className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
                          >
                            {expandedCompanyIds.has(company.company_id) ? (
                              <ExpandLessRoundedIcon fontSize="small" />
                            ) : (
                              <ExpandMoreRoundedIcon fontSize="small" />
                            )}
                            Details
                          </button>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td className="px-5 py-8 text-center text-sm text-slate-500" colSpan={6}>
                      No open billing requests need invoice review.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

        <DeletionJobs jobs={overview?.deletion_jobs} saving={savingJob}
          onAction={(job, action) => saveJobAction(API_update_internal_company_deletion_job, { job_uuid: job.uuid, action })} />

        <section className="space-y-3" aria-label="Company directory">
          <div className="flex items-end gap-4 md:flex-col md:items-stretch">
            <div className="flex-1">
              <h2 className="text-base font-semibold text-slate-900">Company directory</h2>
              <label htmlFor="company-search" className="mt-3 block text-xs font-medium text-slate-600">Search companies, admins, billing emails, or Stripe IDs</label>
              <input id="company-search" type="search" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search companies…"
                className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm outline-none focus:border-violet-600 focus:ring-1 focus:ring-violet-600" />
            </div>
            <div className="w-60 md:w-full">
              <label id="company-filter-label" htmlFor="company-filter" className="mb-1 block text-xs font-medium text-slate-600">Company status</label>
              <RightworkSelect id="company-filter" value={filter} onChange={(event) => setFilter(event.target.value)} options={COMPANY_FILTERS} />
            </div>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-slate-500">
            <p>{visibleCompanies.length} of {companies.length} companies · Expected monthly totals are configured rates, not collected revenue.</p>
            {summary.deletion_requested_companies > 0 && <button type="button" className="font-medium text-rose-700 underline" onClick={() => { setSearch(""); setFilter("deletion_requested"); }}>Review {summary.deletion_requested_companies} deletion requests</button>}
          </div>
          {!visibleCompanies.length && !loadError && <p className="rounded-lg border border-slate-200 bg-white p-8 text-center text-sm text-slate-500">{companies.length ? "No companies match these filters." : "No companies found."}</p>}
          {visibleCompanies.map((company) => {
            const isExpanded = expandedCompanyIds.has(company.company_id);
            const latestInvoice = company.stripe_context?.latest_invoice;
            const stripeConnectionBadge = getStripeConnectionBadge(company);
            const expectedMonthly = getExpectedMonthlyDisplay(company);
            return (
              <div key={company.company_id} className="rounded-lg border border-slate-200 bg-white shadow-sm">
                <div className="flex items-center justify-between gap-4 border-b border-slate-100 px-5 py-4 lg:flex-col lg:items-stretch">
                  <button
                    type="button"
                    onClick={() => toggleCompany(company.company_id)}
                    aria-expanded={isExpanded}
                    className="flex min-w-0 flex-1 items-center gap-3 text-left"
                  >
                    {isExpanded ? <ExpandLessRoundedIcon /> : <ExpandMoreRoundedIcon />}
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="truncate text-base font-semibold text-slate-900">
                          {company.company_name}
                        </h3>
                        <span className={`rounded-full border px-2 py-0.5 text-xs font-medium ${company.company_status === "deletion_requested" ? "border-rose-200 bg-rose-50 text-rose-700" : "border-slate-200 bg-slate-50 text-slate-600"}`}>
                          {COMPANY_STATUS_LABELS[company.company_status] || "Unknown status"}
                        </span>
                        {company.payment_health?.health === "needs_attention" && <span className="rounded-full border border-rose-200 bg-rose-50 px-2 py-0.5 text-xs font-medium text-rose-700">Payment needs attention{company.payment_health.stale ? " · stale" : ""}</span>}
                        {company.needs_invoice && (
                          <span className="rounded-full border border-violet-200 bg-violet-50 px-2 py-0.5 text-xs font-medium text-violet-700">
                            Needs invoice
                          </span>
                        )}
                      </div>
                      <p className="mt-0.5 text-sm text-slate-500">
                        {company.location_count ?? company.stores.length} locations · {company.people?.member_count || 0} team members · {company.billable_store_count} billable - {expectedMonthly.primary} expected monthly
                        {expectedMonthly.detail ? ` (${expectedMonthly.detail})` : ""}
                        {" - "}Stripe {company.stripe_connected ? "connected" : "missing customer"}
                      </p>
                      <CompanyOverdueStatus company={company} />
                      {company.payment_reminders?.[0] && <p className="mt-1 text-xs text-slate-500">Last reminder {company.payment_reminders[0].status === "accepted" ? "accepted" : company.payment_reminders[0].status === "failed" ? "failed" : "unconfirmed"} · {formatDateTime(company.payment_reminders[0].attempted_at)} · {company.payment_reminders[0].actor}</p>}
                    </div>
                  </button>
                  <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
                    {company.stripe_connected && <button type="button" onClick={() => setReminderCompany(company)} className="rounded-lg border border-violet-200 bg-violet-50 px-3 py-2 text-xs font-semibold text-violet-800 hover:bg-violet-100">Draft payment reminder</button>}
                    <span className={`inline-flex items-center gap-1 rounded-lg border px-3 py-2 text-xs font-medium ${stripeConnectionBadge.className}`}>
                      {stripeConnectionBadge.label}
                    </span>
                    {!company.stripe_connected && (
                      <button
                        type="button"
                        onClick={() => setStripeSetupCompany(company)}
                        disabled={Boolean(creatingStripeCustomerCompanyId)}
                        className="inline-flex items-center gap-1 rounded-lg bg-violet-700 px-3 py-2 text-xs font-medium text-white hover:bg-violet-800 disabled:cursor-not-allowed disabled:bg-violet-300"
                      >
                        <CreditCardRoundedIcon style={{ fontSize: "15px" }} />
                        {creatingStripeCustomerCompanyId === company.company_id
                          ? "Creating..."
                          : "Create Stripe customer"}
                      </button>
                    )}
                    {latestInvoice?.hosted_invoice_url && (
                      <a
                        href={latestInvoice.hosted_invoice_url}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50"
                      >
                        <OpenInNewRoundedIcon style={{ fontSize: "15px" }} />
                        Latest invoice
                      </a>
                    )}
                    <button
                      type="button"
                      onClick={() => setEditingCompany(company)}
                      className="inline-flex items-center gap-1 rounded-lg bg-slate-900 px-3 py-2 text-xs font-medium text-white hover:bg-slate-800"
                    >
                      <EditRoundedIcon style={{ fontSize: "15px" }} />
                      Edit rates
                    </button>
                  </div>
                </div>

                {isExpanded && (
                  <div className="px-5 py-4">
                    <CompanyActivityAndPayments company={company} onHealth={updateHealth} />
                    <ReminderHistory history={company.payment_reminders} />
                    <div className="mb-4 grid grid-cols-4 gap-3 lg:grid-cols-2 md:grid-cols-1">
                      <div className="rounded-lg bg-slate-50 p-3">
                        <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">Default rate</p>
                        <p className="mt-1 text-sm font-semibold text-slate-900">
                          {formatMoney(
                            company.default_monthly_store_rate.amount_cents,
                            company.default_monthly_store_rate.currency
                          )}
                        </p>
                      </div>
                      <div className="rounded-lg bg-slate-50 p-3">
                        <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">Stripe customer</p>
                        <p className="mt-1 truncate text-sm font-semibold text-slate-900">
                          {company.billing_settings.stripe_customer_id || "Missing"}
                        </p>
                      </div>
                      <div className="rounded-lg bg-slate-50 p-3">
                        <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">Stripe status</p>
                        <p className="mt-1 text-sm font-semibold text-slate-900">
                          {getStripeLabel(company.stripe_context)}
                        </p>
                      </div>
                      <div className="rounded-lg bg-slate-50 p-3">
                        <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">Requests</p>
                        <p className="mt-1 text-sm font-semibold text-slate-900">
                          {company.pending_billing_requests.length}
                        </p>
                      </div>
                    </div>

                    {company.pending_billing_requests.length > 0 && (
                      <div className="mb-4 rounded-lg border border-violet-100 bg-violet-50 px-4 py-3">
                        <p className="text-sm font-semibold text-violet-950">Open billing requests</p>
                        <div className="mt-2 space-y-2">
                          {company.pending_billing_requests.map((request) => {
                            const requestKey = `${company.company_id}-${request.request_index}`;
                            const isCompleting = completingRequestKey === requestKey;

                            return (
                              <div
                                key={`${request.request_index}-${request.store_id}`}
                                className="flex items-center justify-between gap-3 rounded-lg bg-white/70 px-3 py-2 text-sm text-violet-900 md:flex-col md:items-stretch"
                              >
                                <div className="min-w-0">
                                  <span className="font-medium">{request.store_name || "Unknown store"}</span>
                                  {" - "}
                                  {request.requested_action}
                                  {" - "}
                                  effective {request.effective_date || "not set"}
                                  {" - "}
                                  requested by {request.requested_by_email || "unknown"}
                                </div>
                                <button
                                  type="button"
                                  onClick={() => completeBillingRequest(company, request)}
                                  disabled={Boolean(completingRequestKey)}
                                  className="inline-flex shrink-0 items-center justify-center gap-1.5 rounded-lg border border-violet-200 bg-white px-3 py-1.5 text-xs font-semibold text-violet-700 transition-colors hover:bg-violet-100 disabled:cursor-not-allowed disabled:opacity-60"
                                >
                                  <CheckRoundedIcon style={{ fontSize: "14px" }} />
                                  {isCompleting ? "Completing..." : "Mark complete"}
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    <div className="overflow-x-auto rounded-lg border border-slate-200">
                      <table className="min-w-full divide-y divide-slate-200">
                        <thead className="bg-slate-50">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">Store</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">Status</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">Rate</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">Pending request</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 bg-white">
                          {company.stores.map((store) => {
                            const isUpdatingStoreStatus = updatingStoreStatusId === store.store_id;

                            return (
                              <tr key={store.store_id}>
                                <td className="px-4 py-3">
                                  <p className="text-sm font-medium text-slate-900">{store.store_name}</p>
                                </td>
                                <td className="px-4 py-3">
                                  <div className="space-y-2">
                                    <span className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-medium ${getStatusBadge(store)}`}>
                                      {getBillingStatusLabel(store)}
                                    </span>
                                    <div className="flex flex-wrap gap-2">
                                      {[
                                        { field: "status", label: "Store", options: STORE_STATUS_OPTIONS },
                                        { field: "onboarding_status", label: "Onboarding", options: STORE_ONBOARDING_STATUS_OPTIONS },
                                      ].map(({ field, label, options }) => {
                                        const id = `store-${store.store_id}-${field}`;
                                        return <div key={field} className="min-w-[140px]">
                                          <label id={`${id}-label`} htmlFor={id} className="mb-1 block text-xs text-slate-500">{store.store_name} · {label}</label>
                                          <RightworkSelect id={id} value={store[field]} options={options} disabled={isUpdatingStoreStatus}
                                            onChange={(event) => updateStoreStatus(company, store, field, event.target.value)} />
                                        </div>;
                                      })}
                                    </div>
                                  </div>
                                </td>
                                <td className="px-4 py-3">
                                  <p className="text-sm font-medium text-slate-900">
                                    {formatMoney(
                                      store.effective_monthly_rate.amount_cents,
                                      store.effective_monthly_rate.currency
                                    )}
                                  </p>
                                  <p className="text-xs text-slate-500">
                                    {store.rate_source.replace("_", " ")}
                                  </p>
                                </td>
                                <td className="px-4 py-3 text-sm text-slate-600">
                                  {store.pending_billing_request ? (
                                    <span>
                                      {store.pending_billing_request.requested_action} - effective{" "}
                                      {store.pending_billing_request.effective_date || "not set"}
                                    </span>
                                  ) : (
                                    <span className="text-slate-400">None</span>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                          {company.stores.length === 0 && <tr><td colSpan={4} className="px-4 py-6 text-center text-sm text-slate-500">No locations have been created for this company.</td></tr>}
                        </tbody>
                      </table>
                    </div>
                    <div className="mt-5">
                    <CompanyPeopleDetails company={company} onRequestDeletion={setDeletingCompany} onApproveDeletion={setApprovingCompany}
                      onCancelDeletion={(item) => updateDeletion({ company_id: item.company_id, action: "cancel", request_uuid: item.deletion_request.uuid })}
                      saving={savingDeletion} />
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </section>
      </div>

      {reminderCompany && <PaymentReminderDialog company={reminderCompany} onClose={() => setReminderCompany(null)} onResult={recordReminder} onHealth={updateHealth} />}
      {approvingCompany && <ApproveDeletionDialog key={approvingCompany.company_id} company={approvingCompany} onClose={() => setApprovingCompany(null)} saving={savingJob}
        onSubmit={(payload) => saveJobAction(API_approve_internal_company_deletion, payload)} />}
      {deletingCompany && <DeletionRequestDialog key={deletingCompany.company_id} company={deletingCompany} onClose={() => setDeletingCompany(null)} onSubmit={updateDeletion} saving={savingDeletion} />}
      {stripeSetupCompany && <StripeSetupDialog key={stripeSetupCompany.company_id} company={stripeSetupCompany} onClose={() => setStripeSetupCompany(null)} onSubmit={createStripeCustomer} saving={Boolean(creatingStripeCustomerCompanyId)} />}
      <RateEditModal
        company={editingCompany}
        onClose={() => setEditingCompany(null)}
        onSave={saveRates}
        saving={savingRates}
      />
    </SettingsPageLayout>
  );
}
